import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { TrendingUp, Star, ArrowLeft, Send, CheckCircle, UserCheck, User, Check, Loader2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { cn } from '@/lib/utils';

interface PlayerProfile {
  id: string;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  current_sport: string | null;
  position: string | null;
  team_name: string | null;
  state: string | null;
}
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const SPORTS = [
  'Basketball', 'Football', 'Soccer', 'Baseball', 'Softball', 'Volleyball',
  'Tennis', 'Golf', 'Swimming', 'Track & Field', 'Wrestling', 'Hockey',
  'Lacrosse', 'Gymnastics', 'Cheerleading', 'Other'
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const Nominate: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading: userLoading } = useUnifiedUser();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlayerProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    playerName: '',
    playerUsername: '',
    sport: '',
    position: '',
    school: '',
    stateProvince: '',
    reason: '',
    nominatorRelationship: '',
  });

  // Search for players in the database (both profiles and internal_users)
  const searchPlayers = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    const cleanQuery = query.replace('@', '').toLowerCase();
    
    try {
      // Search profiles table (Supabase auth users)
      const { data: profileUsers } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, current_sport, position, team_name, state')
        .eq('role', 'player')
        .or(`username.ilike.%${cleanQuery}%,first_name.ilike.%${cleanQuery}%,last_name.ilike.%${cleanQuery}%`)
        .limit(10);

      // Search internal_users table by username
      const { data: internalUsers } = await supabase
        .from('internal_users')
        .select('id, username, role')
        .eq('role', 'player')
        .eq('is_active', true)
        .ilike('username', `%${cleanQuery}%`)
        .limit(20);

      // Get internal user profiles if we found internal users
      let internalProfiles: any[] = [];
      if (internalUsers && internalUsers.length > 0) {
        const userIds = internalUsers.map(u => u.id);
        const { data: profiles } = await supabase
          .from('internal_user_profiles')
          .select('user_id, first_name, last_name, current_sport, position, team_name, state_province')
          .in('user_id', userIds);
        internalProfiles = profiles || [];
      }

      // Map internal users to PlayerProfile format
      const internalResults: PlayerProfile[] = (internalUsers || []).map(user => {
        const profile = internalProfiles.find(p => p.user_id === user.id);
        return {
          id: user.id,
          username: user.username,
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          current_sport: profile?.current_sport || null,
          position: profile?.position || null,
          team_name: profile?.team_name || null,
          state: profile?.state_province || null,
        };
      });

      // Also search internal users by first_name/last_name from profiles
      const { data: profileMatches } = await supabase
        .from('internal_user_profiles')
        .select('user_id, first_name, last_name, current_sport, position, team_name, state_province')
        .or(`first_name.ilike.%${cleanQuery}%,last_name.ilike.%${cleanQuery}%`)
        .limit(20);

      // Get user info for profile matches
      if (profileMatches && profileMatches.length > 0) {
        const matchedUserIds = profileMatches.map(p => p.user_id);
        const { data: matchedUsers } = await supabase
          .from('internal_users')
          .select('id, username, role')
          .in('id', matchedUserIds)
          .eq('role', 'player')
          .eq('is_active', true);

        if (matchedUsers) {
          matchedUsers.forEach(user => {
            // Avoid duplicates
            if (!internalResults.find(r => r.id === user.id)) {
              const profile = profileMatches.find(p => p.user_id === user.id);
              internalResults.push({
                id: user.id,
                username: user.username,
                first_name: profile?.first_name || null,
                last_name: profile?.last_name || null,
                current_sport: profile?.current_sport || null,
                position: profile?.position || null,
                team_name: profile?.team_name || null,
                state: profile?.state_province || null,
              });
            }
          });
        }
      }

      // Combine results from both sources
      const combinedResults: PlayerProfile[] = [
        ...(profileUsers || []).map(u => ({ ...u } as PlayerProfile)),
        ...internalResults,
      ];

      // Remove duplicates by id and limit results
      const uniqueResults = combinedResults.filter((player, index, self) =>
        index === self.findIndex(p => p.id === player.id)
      ).slice(0, 15);

      setSearchResults(uniqueResults);
    } catch (error) {
      console.error('Error searching players:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchPlayers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Handle player selection from search
  const handlePlayerSelect = (player: PlayerProfile) => {
    const playerName = [player.first_name, player.last_name]
      .filter(Boolean)
      .join(' ');
    
    const matchedSport = SPORTS.find(s => 
      s.toLowerCase() === player.current_sport?.toLowerCase()
    ) || '';
    
    const matchedState = US_STATES.find(s => 
      s.toLowerCase() === player.state?.toLowerCase()
    ) || '';
    
    setFormData(prev => ({
      ...prev,
      playerName: playerName || prev.playerName,
      playerUsername: player.username ? `@${player.username.replace(/^@/, '')}` : '',
      sport: matchedSport || prev.sport,
      position: player.position || prev.position,
      school: player.team_name || prev.school,
      stateProvince: matchedState || prev.stateProvince,
    }));
    
    setSelectedPlayer(player);
    setIsAutoFilled(true);
    setIsPopoverOpen(false);
    setSearchQuery('');
  };

  // Clear selected player
  const handleClearPlayer = () => {
    setSelectedPlayer(null);
    setIsAutoFilled(false);
    setFormData({
      playerName: '',
      playerUsername: '',
      sport: '',
      position: '',
      school: '',
      stateProvince: '',
      reason: formData.reason,
      nominatorRelationship: formData.nominatorRelationship,
    });
  };

  // Auto-fill form for logged-in player accounts (self-nomination)
  useEffect(() => {
    if (!userLoading && user?.role === 'player' && user?.profile && !selectedPlayer) {
      const profile = user.profile;
      
      const playerName = [profile.first_name, profile.last_name]
        .filter(Boolean)
        .join(' ');
      
      const sport = profile.current_sport || 
                    (profile.preferred_sports?.[0]) || 
                    '';
      
      const matchedSport = SPORTS.find(s => 
        s.toLowerCase() === sport?.toLowerCase()
      ) || '';
      
      const matchedState = US_STATES.find(s => 
        s.toLowerCase() === profile.state?.toLowerCase()
      ) || '';
      
      const hasAutoFillData = playerName || profile.username || matchedSport || 
                              profile.position || profile.team_name || matchedState;
      
      if (hasAutoFillData) {
        setFormData(prev => ({
          ...prev,
          playerName: playerName || prev.playerName,
          playerUsername: profile.username ? `@${profile.username.replace(/^@/, '')}` : prev.playerUsername,
          sport: matchedSport || prev.sport,
          position: profile.position || prev.position,
          school: profile.team_name || prev.school,
          stateProvince: matchedState || prev.stateProvince,
          nominatorRelationship: 'self',
        }));
        setIsAutoFilled(true);
      }
    }
  }, [user, userLoading, selectedPlayer]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: Record<string, string> = {};
    if (!formData.playerName.trim()) errors.playerName = 'Player name is required';
    if (!formData.sport) errors.sport = 'Please select a sport';
    if (!formData.stateProvince) errors.stateProvince = 'Please select a state';
    if (!formData.reason.trim()) errors.reason = 'Please provide a reason for nomination';
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast({
        title: 'Missing Information',
        description: Object.values(errors).join('. '),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from('player_nominations' as any).insert({
        player_name: formData.playerName,
        player_username: formData.playerUsername || null,
        sport: formData.sport,
        position: formData.position || null,
        school: formData.school || null,
        state_province: formData.stateProvince,
        nomination_reason: formData.reason,
        nominator_relationship: formData.nominatorRelationship || null,
        nominator_user_id: user?.id || null,
        nominator_user_type: user?.userType || 'anonymous',
        status: 'pending',
      });

      if (error) throw error;

      setSubmitted(true);
      toast({
        title: 'Nomination Submitted!',
        description: 'Thank you for nominating a rising star. Our team will review it soon.',
      });
    } catch (err) {
      console.error('Error submitting nomination:', err);
      toast({
        title: 'Error',
        description: 'Failed to submit nomination. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 pt-16 pb-8 flex items-start justify-center">
          <Card className="max-w-md w-full text-center mt-8">
            <CardContent className="pt-8 pb-8">
              <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Nomination Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for nominating a rising star. Our team reviews nominations weekly 
                and will feature standout players in the "On the Come Up" section.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button variant="outline" onClick={() => setSubmitted(false)}>
                  Nominate Another
                </Button>
                {user && (
                  <Button variant="outline" onClick={() => navigate('/dashboard')}>
                    Back to Dashboard
                  </Button>
                )}
                <Button onClick={() => navigate('/home')}>
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <TrendingUp className="h-6 w-6 text-primary" />
              Nominate a Player
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            </CardTitle>
            <CardDescription>
              Know a rising star who deserves recognition? Nominate them for our weekly 
              "On the Come Up" feature. Staff reviews all nominations and selects standout 
              players each week.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Auto-fill notice */}
              {isAutoFilled && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      {selectedPlayer 
                        ? `Form filled with ${selectedPlayer.first_name || ''} ${selectedPlayer.last_name || ''}'s profile data.`
                        : 'Form pre-filled with your profile data.'
                      } Review and update if needed.
                    </span>
                  </div>
                  {selectedPlayer && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearPlayer}
                      className="h-7 px-2"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              )}
              
              {/* Player Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground border-b border-border pb-2">
                  Player Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="playerName">Player Name *</Label>
                    <Input
                      id="playerName"
                      placeholder="Full name of the player"
                      value={formData.playerName}
                      onChange={(e) => { handleInputChange('playerName', e.target.value); setFieldErrors(prev => ({ ...prev, playerName: '' })); }}
                      className={fieldErrors.playerName ? 'border-destructive' : ''}
                      required
                    />
                    {fieldErrors.playerName && <p className="text-sm text-destructive">{fieldErrors.playerName}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>PL Username (search players)</Label>
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={isPopoverOpen}
                          className="w-full justify-start text-left font-normal h-10"
                        >
                          <User className="mr-2 h-4 w-4 text-muted-foreground" />
                          {selectedPlayer ? (
                            <span>@{selectedPlayer.username}</span>
                          ) : formData.playerUsername ? (
                            <span>{formData.playerUsername}</span>
                          ) : (
                            <span className="text-muted-foreground">Search for a player...</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput 
                            placeholder="Type username or name..." 
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                          />
                          <CommandList>
                            {isSearching && (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                              </div>
                            )}
                            {!isSearching && searchResults.length === 0 && searchQuery.length >= 2 && (
                              <CommandEmpty>No players found</CommandEmpty>
                            )}
                            {!isSearching && searchQuery.length < 2 && searchQuery.length > 0 && (
                              <div className="py-4 text-center text-sm text-muted-foreground">
                                Type at least 2 characters to search
                              </div>
                            )}
                            {!isSearching && searchResults.length > 0 && (
                              <CommandGroup heading="Players">
                                {searchResults.map((player) => (
                                  <CommandItem
                                    key={player.id}
                                    value={player.username || player.id}
                                    onSelect={() => handlePlayerSelect(player)}
                                    className="cursor-pointer"
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", 
                                      selectedPlayer?.id === player.id ? "opacity-100" : "opacity-0"
                                    )} />
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        @{player.username} - {player.first_name} {player.last_name}
                                      </span>
                                      {player.current_sport && (
                                        <span className="text-xs text-muted-foreground">
                                          {player.current_sport} {player.position && `| ${player.position}`}
                                        </span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sport">Sport *</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) => { handleInputChange('sport', value); setFieldErrors(prev => ({ ...prev, sport: '' })); }}
                    >
                      <SelectTrigger className={fieldErrors.sport ? 'border-destructive' : ''}>
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS.map(sport => (
                          <SelectItem key={sport} value={sport}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.sport && <p className="text-sm text-destructive">{fieldErrors.sport}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="position">Position</Label>
                    <Input
                      id="position"
                      placeholder="e.g., Point Guard, Quarterback"
                      value={formData.position}
                      onChange={(e) => handleInputChange('position', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="school">School/Team</Label>
                    <Input
                      id="school"
                      placeholder="School or team name"
                      value={formData.school}
                      onChange={(e) => handleInputChange('school', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="stateProvince">State *</Label>
                    <Select
                      value={formData.stateProvince}
                      onValueChange={(value) => { handleInputChange('stateProvince', value); setFieldErrors(prev => ({ ...prev, stateProvince: '' })); }}
                    >
                      <SelectTrigger className={fieldErrors.stateProvince ? 'border-destructive' : ''}>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.stateProvince && <p className="text-sm text-destructive">{fieldErrors.stateProvince}</p>}
                  </div>
                </div>
              </div>

              {/* Nomination Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground border-b border-border pb-2">
                  Why This Player?
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Nomination *</Label>
                  <Textarea
                    id="reason"
                    placeholder="Tell us why this player deserves to be featured. What makes them stand out? Recent achievements, skills, character..."
                    value={formData.reason}
                    onChange={(e) => { handleInputChange('reason', e.target.value); setFieldErrors(prev => ({ ...prev, reason: '' })); }}
                    className={cn("min-h-[120px]", fieldErrors.reason && 'border-destructive')}
                    required
                  />
                  {fieldErrors.reason && <p className="text-sm text-destructive">{fieldErrors.reason}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="relationship">Your Relationship to Player</Label>
                  <Select
                    value={formData.nominatorRelationship}
                    onValueChange={(value) => handleInputChange('nominatorRelationship', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="parent">Parent/Guardian</SelectItem>
                      <SelectItem value="teammate">Teammate</SelectItem>
                      <SelectItem value="fan">Fan</SelectItem>
                      <SelectItem value="self">Self-Nomination</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Nomination
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default Nominate;
